import { useEffect, useState } from 'react';
import type { TabKey } from './sourceTabMetadata';

type StatsFileKey = 'gdas' | 'vdas';

type TabStatsConfig = {
  file: StatsFileKey;
  column: string;
};

const TAB_STATS_CONFIG: Partial<Record<TabKey, TabStatsConfig>> = {
  GDA: { file: 'gdas', column: 'gdas' },
  DISEASES: { file: 'gdas', column: 'diseases' },
  GENES: { file: 'gdas', column: 'genes' },
  VDA: { file: 'vdas', column: 'vevidences' },
  VARIANTS: { file: 'vdas', column: 'variants' },
};

const STATS_FILE_PATHS: Record<StatsFileKey, string> = {
  gdas: '/static/files/gdas_stats.tsv',
  vdas: '/static/files/vdas_stats.tsv',
};

const statsCache: Partial<Record<StatsFileKey, Record<string, Record<string, number>>>> = {};

async function loadStatsFile(fileKey: StatsFileKey): Promise<Record<string, Record<string, number>>> {
  if (statsCache[fileKey]) {
    return statsCache[fileKey]!;
  }

  const response = await fetch(STATS_FILE_PATHS[fileKey]);
  const text = await response.text();
  const lines = text.trim().split('\n');
  const headers = lines[0].split('\t');
  const bySource: Record<string, Record<string, number>> = {};

  for (let i = 1; i < lines.length; i += 1) {
    const line = lines[i]?.trim();
    if (!line) continue;
    const values = line.split('\t');
    const sourceName = values[0];
    if (!sourceName) continue;
    bySource[sourceName] = {};
    headers.forEach((header, index) => {
      const parsed = Number(values[index]);
      if (!Number.isNaN(parsed)) {
        bySource[sourceName][header] = parsed;
      }
    });
  }

  statsCache[fileKey] = bySource;
  return bySource;
}

export async function getSourceTabRecordCount(source: string, tab: string): Promise<number | null> {
  const config = TAB_STATS_CONFIG[tab as TabKey];
  if (!config) return null;

  const stats = await loadStatsFile(config.file);
  const sourceStats = stats[source];
  if (!sourceStats) return null;

  const count = sourceStats[config.column];
  return typeof count === 'number' ? count : null;
}

export function formatRecordCount(count: number): string {
  return count.toLocaleString('en-US');
}

export function useSourceTabStats(source: string, tabs: string[]) {
  const [counts, setCounts] = useState<Record<string, number | null>>({});

  useEffect(() => {
    let cancelled = false;

    const loadCounts = async () => {
      const entries = await Promise.all(
        tabs.map(async tab => {
          const count = await getSourceTabRecordCount(source, tab);
          return [tab, count] as const;
        }),
      );

      if (!cancelled) {
        setCounts(Object.fromEntries(entries));
      }
    };

    loadCounts().catch(() => {
      if (!cancelled) {
        setCounts({});
      }
    });

    return () => {
      cancelled = true;
    };
  }, [source, tabs.join('|')]);

  return counts;
}
